import React, { FC, memo, useState, useCallback, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { ReactComponent as LocationIcon } from 'shared/assets/icons/LocationIcon.svg';
import NoResultsImage from 'shared/assets/images/NoResultsImage.svg';
import { getLoggedUserData, useGetUserReservationsQuery, ProfileVerificationStatus, UserAccountType } from 'entities/User';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { NoResults } from 'shared/ui/NoResults';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useNavigate } from 'react-router-dom';
import { useGetUserBookingsQuery } from 'entities/Contract';
import { PageLoader } from 'features/PageLoader';
import { GratitudeModal } from 'features/GratitudeModal';
import { CopyTooltip } from 'shared/ui/CopyTooltip';

export const MyBoxesPage: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);
  const navigate = useNavigate();

  const loggedUserData = useAppSelector(getLoggedUserData);

  const { data: reservations, isLoading: reservationsLoading } = useGetUserReservationsQuery(
    { userId: loggedUserData?.userId, active: true },
    {
      skip: !loggedUserData?.userId,
    },
  );

  const { data: bookings, isLoading: bookingsLoading } = useGetUserBookingsQuery(loggedUserData?.userId, {
    skip: !loggedUserData?.userId,
  });

  const goToBookingPage = (): void => {
    navigate(AppRoutes.BOOKING);
  };

  const goToMyBookingsPage = useCallback(
    (warehouseId: string): void => {
      navigate(`${AppRoutes.MY_BOXES}/${warehouseId}`);
    },
    [navigate],
  );

  const locations = useMemo(() => {
    const bookingLocations =
      bookings?.map((warehouse) => {
        return {
          warehouseId: warehouse.warehouseId,
          address: warehouse?.address,
          zip: warehouse?.zip,
          city: warehouse?.city?.name,
          imageUrl: warehouse?.imageUrl,
          name: warehouse?.name,
        };
      }) || [];

    const reservationLocations =
      reservations?.map((reservation) => {
        return {
          warehouseId: reservation.warehouse.warehouseId,
          address: reservation.warehouse?.address,
          zip: reservation.warehouse?.zip,
          city: reservation.warehouse?.city?.name,
          imageUrl: reservation.warehouse?.imageUrl,
          name: reservation.warehouse?.name,
        };
      }) || [];

    return _.uniqBy([...bookingLocations, ...reservationLocations], 'warehouseId');
  }, [bookings, reservations]);

  // useEffect(() => {
  //   if (locations.length === 1) {
  //     goToMyBookingsPage(locations[0].warehouseId);
  //   }
  // }, [bookings, goToMyBookingsPage, locations]);

  if (reservationsLoading || bookingsLoading) {
    return <PageLoader />;
  }

  return (
    <div className="max-w-[1200px] mx-auto">
      {/* <GratitudeModal /> */}
      {!_.isEmpty(locations) ? (
        <div className="flex gap-1 desktop:gap-4 flex-wrap py-2 px-4 justify-center desktop:justify-start desktop:pt-7">
          {locations?.map((warehouse) => {
            const fullAddress = `${warehouse?.address} ${warehouse?.zip || ''} ${warehouse.city || ''}`;

            return (
              <div
                key={warehouse.warehouseId}
                className="p-3 desktop:p-4 border rounded-xl flex flex-col gap-2 justify-center w-fit bg-white"
              >
                <img
                  className="min-w-[329px] max-h-[215px] desktop:w-[329px] desktop:h-[215px] object-cover rounded-md"
                  src={warehouse.imageUrl || ''}
                  alt={`Warehouse ${warehouse.name}`}
                />
                <div className="flex flex-col mb-2">
                  <div className="font-semibold text-2xl desktop:text-3xl font-tektur">{warehouse.name}</div>
                  <div className="flex items-center mt-1">
                    <LocationIcon className="mr-2" />
                    <CopyTooltip className="cursor-pointer text-grey desktop:text-lg">{fullAddress}</CopyTooltip>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    goToMyBookingsPage(warehouse.warehouseId);
                  }}
                >
                  {t('Go', { ns: 'common' })}
                </Button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="px-4">
          <NoResults
            image={NoResultsImage}
            title={t('Looks like it`s empty here', { ns: 'common' })}
            description={t('Oops, you don`t have reservations or rental boxes. Go to the booking page if you would like to rent a box', {
              ns: 'common',
            })}
            actions={
              <Button containerClassName="w-full desktop:w-auto" size="medium" onClick={goToBookingPage}>
                {t('Go to booking')}
              </Button>
            }
          />
        </div>
      )}
    </div>
  );
});
